import { motion } from "framer-motion";
import { ArrowUpRight, Phone, Instagram, MapPin } from "lucide-react";
import { SITE, TikTokIcon } from "../lib/site";
import { Reveal, MaskedLine } from "./Reveal";

export const Contact = () => (
    <section id="book" data-testid="contact-section" className="relative py-24 lg:py-36 overflow-hidden">
        <div
            className="absolute inset-0 -z-10 opacity-[0.06]"
            style={{
                backgroundImage:
                    "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
                backgroundSize: "72px 72px",
            }}
            aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
                <p className="font-code text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6">
                    Book Your Slot
                </p>
            </Reveal>

            <h2 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl">
                <MaskedLineInView>Ready when</MaskedLineInView>
                <MaskedLineInView delay={0.15}>
                    <span className="italic text-zinc-500">you are.</span>
                </MaskedLineInView>
            </h2>

            <Reveal delay={0.3}>
                <p className="mt-7 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed">
                    Grab a time that works for you on Booksy, or call and we'll sort it out.
                    We bring everything — you just hand over the keys.
                </p>
            </Reveal>

            <Reveal delay={0.4}>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                    <motion.a
                        data-testid="contact-booksy-button"
                        href={SITE.booksy}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        className="inline-flex items-center gap-2 bg-white text-black font-semibold px-9 py-4.5 rounded-full text-base py-4"
                    >
                        Book on Booksy <ArrowUpRight className="w-5 h-5" />
                    </motion.a>
                    <motion.a
                        data-testid="contact-call-button"
                        href={SITE.phoneHref}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        className="inline-flex items-center gap-2 border border-white/25 hover:border-white/60 transition-colors duration-300 font-semibold px-9 py-4 rounded-full text-base"
                    >
                        <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
                    </motion.a>
                </div>
            </Reveal>

            <Reveal delay={0.5}>
                <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
                    <div data-testid="contact-area-card" className="flex items-center gap-3 border border-white/10 rounded-2xl px-5 py-4 bg-[#121216]/60">
                        <MapPin className="w-4 h-4 text-zinc-500 shrink-0" />
                        <span className="text-sm text-zinc-300">{SITE.area}</span>
                    </div>
                    <a
                        data-testid="contact-instagram-link"
                        href={SITE.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 border border-white/10 hover:border-white/40 transition-colors duration-300 rounded-2xl px-5 py-4 bg-[#121216]/60"
                    >
                        <Instagram className="w-4 h-4 text-zinc-500 shrink-0" />
                        <span className="text-sm text-zinc-300">@pronto.autocare</span>
                    </a>
                    <a
                        data-testid="contact-tiktok-link"
                        href={SITE.tiktok}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 border border-white/10 hover:border-white/40 transition-colors duration-300 rounded-2xl px-5 py-4 bg-[#121216]/60"
                    >
                        <TikTokIcon className="w-4 h-4 text-zinc-500 shrink-0" />
                        <span className="text-sm text-zinc-300">@pronto.autocare</span>
                    </a>
                </div>
            </Reveal>
        </div>
    </section>
);

const MaskedLineInView = ({ children, delay = 0 }) => (
    <span className="block overflow-hidden">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            whileInView={{ y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);
